import Style from './studyNoteItem.module.scss';
import { DropDownMenu } from 'uikit/dropDownMenu/dropDownMenu.component';
import { ReactComponent as Dots } from 'assets/img/common/dots_24.svg';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { IStudyNoteConfigDto, IStudyNoteResultDto } from 'src/services/api/api-client';
import { DateFormats, localFormat } from 'src/helpers/date-helpers';
import { useTranslation } from 'react-i18next';
import { DataType } from 'src/components/formEditor/uiEditor/uiEditor';
import { useForm } from 'react-hook-form';
import { GeneralFormAnswer } from 'src/features/generalForms/answerPreview/generalFormAnswer';
import { studyNoteAnswerControlsResolver } from 'src/features/generalForms/studyNoteForms/answerResolver';
import { DropDownMenuOption } from 'uikit/dropDownMenu/dropDownMenu';
import { useStudyNoteFillingModal } from './useStudyNoteFillingModal';
import { removeStudyNote } from 'src/services/api/api-client/StudyNotesClient';
import { useStudy } from 'src/helpers/hooks/useStudy';
import { getStudyNotesQueryKey } from 'src/services/api/api-client/StudyNotesQuery';
import { useQueryClient } from '@tanstack/react-query';
import { showServerErrorToast } from 'src/components/toast/toast-helper';
import { ConfirmResult, useConfirmationModal } from 'src/components/dialogModal/useConfirmationModal';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';
import { useStudyNoteExport } from './useStudyNoteExport';

export const StudyNoteItem: FC<{ note: IStudyNoteResultDto; config: IStudyNoteConfigDto }> = ({ config, note }) => {
  return (
    <div className={Style.container}>
      <NoteHeader note={note} config={config} />
      {config && <StudyNoteAnswerView config={config} values={note.value as DataType} />}
    </div>
  );
};

const NoteHeader: FC<{ note: IStudyNoteResultDto; config: IStudyNoteConfigDto }> = ({ note, config }) => {
  const { t } = useScopedTranslation('StudyInfoPage.StudyNotesSection.StudyNoteItem');
  const { t: tCommon } = useTranslation();
  const study = useStudy();
  const queryClient = useQueryClient();
  const { modal, openHandler } = useStudyNoteFillingModal();
  const removeConfirmationModal = useConfirmationModal();

  const { exportHandler } = useStudyNoteExport([{ result: note, config: config }]);

  const removeNoteHandler = useCallback(async () => {
    try {
      const result = await removeConfirmationModal.open({
        title: t('RemoveConfirmationModal.Title'),
        text: t('RemoveConfirmationModal.Text'),
        okButtonColorSchema: 'destroy',
        cancelButtonText: t('RemoveConfirmationModal.CancelButtonText'),
        okButtonText: t('RemoveConfirmationModal.OkButtonText'),
      });

      if (result !== ConfirmResult.Confirm) return;

      await removeStudyNote(study!.id, note.formResultId);
      await queryClient.invalidateQueries(getStudyNotesQueryKey(study!.id));
    } catch (error) {
      showServerErrorToast(error);
    }
  }, [note.formResultId, queryClient, removeConfirmationModal, study, t]);

  const menuOptions = useMemo(
    () =>
      [
        {
          key: 0,
          text: tCommon('Common_Edit'),
          variant: 'primary',
          action: () => openHandler(note, config),
        },
        {
          key: 1,
          text: tCommon('Common_Export'),
          variant: 'primary',
          action: () => exportHandler(),
        },
        {
          key: 2,
          text: tCommon('Common_Delete'),
          variant: 'danger',
          action: removeNoteHandler,
        },
      ] satisfies DropDownMenuOption[],
    [config, note, exportHandler, openHandler, removeNoteHandler, tCommon],
  );

  return (
    <div>
      <div className={Style.header}>
        <div className={Style.createdAtDate}>
          <div>{localFormat(note.createdAt, DateFormats.longDate)}</div>
          <DropDownMenu Icon={Dots} options={menuOptions} colorSchema={'basic'} />
        </div>
        <div className={Style.updatedAtDate}>
          {note.updatedAt.getTime() === note.createdAt.getTime()
            ? t('CreatedAt', {
                date: localFormat(note.createdAt, DateFormats.longDate),
              })
            : t('UpdatedAt', {
                date: localFormat(note.updatedAt, DateFormats.longDate),
              })}
        </div>
      </div>
      {modal}
      {removeConfirmationModal.modal}
    </div>
  );
};

const StudyNoteAnswerView: FC<{ config: IStudyNoteConfigDto; values: DataType }> = ({ config, values }) => {
  const form = useForm<DataType>({
    defaultValues: values,
  });

  useEffect(() => {
    form.reset(values);
  }, [form, values]);

  return (
    <GeneralFormAnswer
      form={form}
      resolver={studyNoteAnswerControlsResolver}
      defaultLayout={config.layout ?? {}}
      initialScoreSections={config.scoreSections ?? []}
      initialVariables={config.variables ?? []}
    />
  );
};
